import { Alert, Drawer, DrawerHeader, DrawerBody, Link, Typography, DrawerFooter, Button, } from '@one-thd/sui-atomic-components';
import React, { useEffect, useState } from 'react';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { getWindow, isLLC } from '../../utils/general';
import { useExperienceContext } from '../../hooks/useContexts';
import { useQSIApi } from '../../hooks/useQSIApi';
var phxSurveyInterceptId = isLLC()
    ? 'SI_3CScACQKQkjVr4W'
    : 'SI_eP8ecJKmdNSxo6W';
export var THIRTY_DAYS_IN_SECONDS = 30 * 24 * 60 * 60;
/**
 * Returns the timestamp for a given survey intercept key from localStorage.Q_INTER.
 * @returns The timestamp (number) or undefined if not found or invalid.
 */
export function getQualtricsSurveyTimestamp() {
    var _a;
    try {
        var qInter = (_a = getWindow()) === null || _a === void 0 ? void 0 : _a.localStorage.getItem('Q_INTER');
        if (!qInter)
            return undefined;
        var parsed = JSON.parse(qInter);
        var ts = parsed[phxSurveyInterceptId];
        return typeof ts === 'number' ? ts : undefined;
    }
    catch (_b) {
        return undefined;
    }
}
/**
 * Returns the timestamp for PHX_Survey_TS_Dismiss from localStorage.
 * @returns The timestamp (number) or undefined if not found or invalid.
 */
export function getPHXDismissSurveyTimestamp() {
    var _a;
    try {
        var ts = (_a = getWindow()) === null || _a === void 0 ? void 0 : _a.localStorage.getItem('PHX_Survey_TS_Dismiss');
        if (!ts)
            return undefined;
        var num = Number(ts);
        return Number.isFinite(num) ? num : undefined;
    }
    catch (_b) {
        return undefined;
    }
}
/**
 * Determines if the PHX survey banner should be shown.
 * @returns true if the banner should be shown, false otherwise.
 */
export function shouldShowSurveyBanner() {
    var qualtricsTimestamp = getQualtricsSurveyTimestamp();
    var phxDismissTimestamp = getPHXDismissSurveyTimestamp();
    if (qualtricsTimestamp === undefined && phxDismissTimestamp === undefined)
        return true;
    var now = Math.floor(Date.now() / 1000);
    var qualtricsExpired = qualtricsTimestamp !== undefined
        ? now >= qualtricsTimestamp + THIRTY_DAYS_IN_SECONDS
        : false;
    var phxDismissExpired = phxDismissTimestamp !== undefined
        ? now >= phxDismissTimestamp + THIRTY_DAYS_IN_SECONDS
        : false;
    return qualtricsExpired && phxDismissExpired;
}
export var PHXSatisfactionSurvey = function () {
    var _a = useThdCustomer(), customerType = _a.customerType, svocID = _a.svocID;
    var _b = useExperienceContext(), isConsumerApp = _b.isConsumerApp, channel = _b.channel;
    var _c = useState(false), showSurveyBanner = _c[0], setShowSurveyBanner = _c[1];
    var _d = useState(false), openSurveyDrawer = _d[0], setOpenSurveyDrawer = _d[1];
    useEffect(function () {
        setShowSurveyBanner(shouldShowSurveyBanner());
    }, []);
    // Hook to trigger QSI API when the survey drawer is opened
    useQSIApi({ trigger: openSurveyDrawer });
    // Determine if the customer is on a mobile device or tablet or using the consumer app
    var isOnMobile = channel === 'mobile' || channel === 'tablet' || isConsumerApp
        ? 'true'
        : 'false';
    // Determine if svocID is available, otherwise default to 'guest'
    var hasSvocId = svocID || 'guest';
    // Handle close of the survey banner
    var handleCloseBanner = function () {
        var _a;
        // create a timestamp value that sets the current time
        var timestamp = Math.floor(Date.now() / 1000);
        (_a = getWindow()) === null || _a === void 0 ? void 0 : _a.localStorage.setItem('PHX_Survey_TS_Dismiss', timestamp.toString());
        setShowSurveyBanner(false);
    };
    // Handle closing the survey drawer
    var handleCloseDrawer = function () {
        setShowSurveyBanner(false);
        setOpenSurveyDrawer(false);
    };
    /**
     * Render the PHX Satisfaction Survey component.
     * Displays a banner prompting the user to take a survey.
     * When the banner is clicked, a drawer opens with the survey embedded.
     * The survey includes hidden inputs for customer type, channel, mobile status,
     * and svocID, which are used by the survey script.
     * The drawer can be closed, which also hides the banner.
     */
    return (React.createElement("div", { className: "sui-w-full sui-py-3" },
        showSurveyBanner && (React.createElement(Alert, { status: "info", size: "large", onClose: function () {
                handleCloseBanner();
            } },
            React.createElement("div", { className: "sui-flex sui-flex-col" },
                React.createElement(Typography, { weight: "bold" }, "We would love to hear from you!"),
                React.createElement("div", { className: "sui-flex sui-items-center sui-gap-1" },
                    React.createElement(Typography, null, "Take a "),
                    React.createElement(Link, { component: "button", onClick: function () { return setOpenSurveyDrawer(true); } }, "quick survey"))))),
        React.createElement(Drawer, { open: openSurveyDrawer, onClose: handleCloseDrawer, position: "right" },
            React.createElement(DrawerHeader, { title: "Help us improve your experience", onClose: handleCloseDrawer }),
            React.createElement(DrawerBody, null,
                React.createElement("div", null,
                    React.createElement("input", { type: "hidden", id: "customerTypeForPHX", value: customerType }),
                    React.createElement("input", { type: "hidden", id: "siteXPForPHX", value: channel }),
                    React.createElement("input", { type: "hidden", id: "appXPforPHX", value: isOnMobile }),
                    React.createElement("input", { type: "hidden", id: "svocidforPHX", value: hasSvocId }),
                    React.createElement("div", { id: "PHXSatisfactionAndEase", className: "sui-w-full sui-h-full", style: {
                            display: 'block',
                        } }))),
            React.createElement(DrawerFooter, null,
                React.createElement(Button, { fullWidth: true, onClick: handleCloseDrawer, "data-testid": "close-phx-satisfaction-survey-drawer" }, "Close")))));
};
